import { useCallback, useEffect, useRef, useState } from 'react';
import { TransferringCall } from '@app/layouts/MainLayout/components/CallBar/components/TransferringCall/TransferringCall';
import { FeatureErrorBoundary } from '@components/ErrorBoundary';
import usePhoneState from '@modules/SoftPhoneModule/usePhoneState';
import TasksModule from '@modules/TasksModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import { PIP_EVENTS } from '../constants';

/**
 * Always-mounted component that listens for the pip:open-transfer event
 * and opens the transfer modal in the main window.
 */
export function PipTransferTrigger() {
  const [open, setOpen] = useState(false);
  const [task, setTask] = useState<any>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const { callList } = usePhoneState();

  const confirmedActiveRef = useRef(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    setTask(null);
    setCallId(null);
    confirmedActiveRef.current = false;
  }, []);

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const id = (event as CustomEvent<string>).detail;
      if (id) {
        const callTask = TasksModule.getTaskByCallId(id);
        if (callTask) {
          confirmedActiveRef.current = false;
          setTask(callTask);
          setCallId(id);
          setOpen(true);
          SoftPhoneModule.callControl.hold();
        }
      }
    };
    window.addEventListener(PIP_EVENTS.OPEN_TRANSFER, handleOpen);
    return () =>
      window.removeEventListener(PIP_EVENTS.OPEN_TRANSFER, handleOpen);
  }, []);

  // Close when the call that initiated the transfer ends,
  // mirroring how CallBar unmounts the modal when isInCall becomes false.
  // Only close after the call has been confirmed active at least once
  // to guard against transient callList states during the hold transition.
  useEffect(() => {
    if (!open || !callId) return;
    const callStillActive = callList.some((call) => call.callId === callId);
    if (callStillActive) {
      confirmedActiveRef.current = true;
    } else if (confirmedActiveRef.current) {
      handleClose();
    }
  }, [open, callId, callList, handleClose]);

  if (!open || !task) return null;

  return (
    <FeatureErrorBoundary featureName='PiP Transfer Call' size='full'>
      <TransferringCall task={task} onClose={handleClose} />
    </FeatureErrorBoundary>
  );
}
